import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SITE_ACCESS_PAGES, SiteAccessService } from '../services/site-access.service';
import { decideAccess } from '../services/site-access.logic';

/**
 * Applies the admin Site Access switch to a route: put `data: { accessId: 'optionsDesk' }` on it.
 *   public  - open to everyone.
 *   members - opened for admins and users granted the page's feature key.
 *   admin   - opened for admins only.
 * Anyone else is sent to the page preview (/members-only/:pageId), which shows what the page offers with sign-in and
 * request-access buttons (tagged "coming soon" for an Admin only page). A page with no setting (or an unreadable one) uses
 * its default.
 *
 * AlgoDesk has its own members check and its own locked screen, so Members only leaves it alone. Renko's members rule
 * is just "signed in", which this guard applies itself.
 */
@Injectable({ providedIn: 'root' })
export class SiteAccessGuard implements CanActivate {
  constructor(private auth: AuthService, private access: SiteAccessService, private router: Router) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const page = SITE_ACCESS_PAGES.find(p => p.id === route.data['accessId']);
    const level = page ? await this.access.get(page.id) : 'public';
    if (!page || level === 'public') return true;
    // AlgoDesk keeps its own members check and locked screen, so Members only needs no sign-in wait here.
    if (level === 'members' && !page.feature && page.id !== 'renko') return true;

    await this.ready();
    const decision = decideAccess(page, level, {
      signedIn: !!this.auth.currentUser,
      isAdmin: this.auth.isAdmin,
      hasFeature: key => this.auth.hasFeature(key),
      canAccessLevelData: this.auth.canAccessLevelData,
    });
    return decision === 'allow' ? true : this.preview(page.id);
  }

  private async ready(): Promise<void> {
    await this.auth.ensureReady();
    await firstValueFrom(this.auth.loading$.pipe(filter(loading => !loading)));
  }

  private preview(pageId: string): UrlTree {
    return this.router.createUrlTree(['/members-only', pageId]);
  }
}
